import type { AuthTokens } from '@nutrigeist/shared';

/**
 * HTTP client for the NutriGeist API.
 *
 * Access tokens are short-lived, so a 401 mid-session is normal rather than
 * exceptional. The client transparently refreshes and replays the request once.
 * Refreshes are single-flighted: a screen that fires five queries at once on
 * wake-up must not burn five refresh tokens, and since the server rotates and
 * revokes on every use, the losers of that race would invalidate the session.
 */

const ACCESS_TOKEN_KEY = 'nutrigeist.accessToken';
const REFRESH_TOKEN_KEY = 'nutrigeist.refreshToken';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Field-keyed validation messages, when the failure was a 400. */
  get fieldErrors(): Record<string, string[]> | null {
    if (this.code !== 'VALIDATION_FAILED' || typeof this.details !== 'object' || !this.details) {
      return null;
    }
    return this.details as Record<string, string[]>;
  }
}

export const tokenStore = {
  get accessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },
  get refreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },
  save(tokens: AuthTokens): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  },
  clear(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

/** Notifies the app when the session ends so it can route back to login. */
type SessionExpiredListener = () => void;
let onSessionExpired: SessionExpiredListener | null = null;

export function setSessionExpiredHandler(listener: SessionExpiredListener | null): void {
  onSessionExpired = listener;
}

/** In-flight refresh, shared by every caller that hits a 401 concurrently. */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const refreshToken = tokenStore.refreshToken;
  if (!refreshToken) return false;

  const response = await fetch('/api/auth/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    tokenStore.clear();
    return false;
  }

  tokenStore.save((await response.json()) as AuthTokens);
  return true;
}

function ensureRefresh(): Promise<boolean> {
  refreshInFlight ??= refreshTokens().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Sent as-is instead of being JSON-encoded — used for image uploads. */
  formData?: FormData;
  query?: Record<string, string | number | boolean | undefined>;
  /** Skips the Authorization header, for login and registration. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

/**
 * Performs the request, refreshing the access token once if it has expired.
 *
 * Shared by the JSON and file-download paths so a download cannot end up with
 * its own, subtly different session handling.
 */
async function requestWithRefresh(path: string, options: RequestOptions): Promise<Response> {
  const response = await performRequest(path, options);

  // One retry after a successful refresh; a second 401 means the session is
  // genuinely gone.
  if (response.status === 401 && !options.anonymous) {
    const refreshed = await ensureRefresh();

    if (!refreshed) {
      tokenStore.clear();
      onSessionExpired?.();
      throw await toApiError(response);
    }

    const retried = await performRequest(path, options);
    if (retried.status === 401) {
      tokenStore.clear();
      onSessionExpired?.();
    }
    return retried;
  }

  return response;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return handle<T>(await requestWithRefresh(path, options));
}

/**
 * Fetches a file and hands it to the browser as a download.
 *
 * Deliberately not a plain `<a href>`: these endpoints need an Authorization
 * header, which a link cannot send. The filename comes from the server's
 * `content-disposition` so the date in it is the server's, not the device's.
 */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const response = await requestWithRefresh(path, {});
  if (!response.ok) throw await toApiError(response);

  const disposition = response.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? fallbackName;

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Give the browser a moment to start the download before the blob goes.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}

async function performRequest(path: string, options: RequestOptions): Promise<Response> {
  const url = new URL(path, window.location.origin);

  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = {};

  if (!options.anonymous) {
    const token = tokenStore.accessToken;
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  // FormData must set its own multipart boundary, so no Content-Type here.
  if (options.body !== undefined && !options.formData) {
    headers['Content-Type'] = 'application/json';
  }

  return fetch(url.toString(), {
    method: options.method ?? (options.body || options.formData ? 'POST' : 'GET'),
    headers,
    body:
      options.formData ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
    signal: options.signal,
  });
}

async function handle<T>(response: Response): Promise<T> {
  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) return undefined as T;

  return (await response.json()) as T;
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = 'UNKNOWN';
  let message = `Request failed with status ${response.status}`;
  let details: unknown;

  try {
    const body = (await response.json()) as {
      error?: { code?: string; message?: string; details?: unknown };
    };
    if (body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details;
    }
  } catch {
    // Non-JSON error body (a proxy error page); keep the defaults.
  }

  return new ApiError(response.status, code, message, details);
}
